// src/pages/ModuleAssignment.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import RoleCreationModal from "./RoleCreationModal";
import { useQueryClient } from "@tanstack/react-query";
import hub from "../services/EventHub";

/* Ant Design v6 */
import { App, Alert, Button, Checkbox, Modal, Select, Skeleton, Tooltip, Typography } from "antd";
import {
  DetailList,
  PageHeader,
  Section,
  SectionCard,
  StatStrip,
  StatusTag,
  TabBar,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import { ListToolbar } from "../components/ui";
import notify from "../utils/notify";
import { COMPONENT_GROUP_MAP, GROUP_DEFS, GROUP_ITEM_ORDER } from "../components/DynamicMenu/index"; // explicit: components/DynamicMenu.js (old) would win otherwise

const { Text } = Typography;

/* --- Friendly labels (unchanged) --------------------------------------- */
const menuLabelMap = {
  AboutUsPage: "About Us",
  AdminRegistrationPage: "Admin Registration",
  AmenitiesPage: "Amenities",
  AmenitiesListPage: "Amenities List",

  LandingPage: "Home",
  Login: "Login",
  Registration: "Register",
  ResetPassword: "Reset Password",
  ForgotPassword: "Forgot Password",
  Unauthorized: "Unauthorized",
  EmailConfirmation: "Confirm Email",
  ResendConfirmationPage: "Resend Confirmation",
  EmailConfirmationPendingPage: "Email Confirmation Pending",

  Dashboard: "Dashboard",
  AdminDashboard: "Admin Dashboard",
  Settings: "Settings",
  SettingsPage: "Settings Page",
  ProfileSettingsPage: "Profile Settings",
  AccountSettingsPage: "Account Settings",

  Hotel: "Hotel",
  HotelDashboardCards: "Hotel Dashboard Cards",
  HotelEditModal: "Edit Hotel",
  HotelForm: "Hotel Form",
  HotelFormStep1A: "Hotel Form Step 1A",
  HotelFormStep1AA: "Hotel Form Step 1AA",
  HotelFormStep1B: "Hotel Form Step 1B",
  HotelFormStep2: "Hotel Form Step 2",
  HotelFormStep3: "Hotel Form Step 3",
  HotelFormStep5Registration: "Hotel Registration",
  HotelList: "Hotel List",
  HotelProfile: "Hotel Profile",
  HotelProfileHeader: "Hotel Profile Header",
  HotelProfilePage: "Hotel Profile",
  Hotels: "Hotels",
  BookingListPage: "Booking List",
  BookingModal: "Booking Modal",
  BookingPage: "Booking",
  BookingTypesPage: "Booking Types",
  Breadcrumb: "Breadcrumb",
  Calendar: "Calendar",
  ChangeUserRoleModal: "Change User Role",
  ChatSidebar: "Chat Sidebar",
  ChatWindow: "Chat Window",
  CheckInOutPage: "Check In/Out",
  ChecklistModal: "Checklist",
  CheckOut: "Check Out",
  CompleteUserProfile: "Complete Profile",
  ContactUsPage: "Contact Us",
  countries: "Countries",
  CreateServicePage: "Create Service",
  CreateTaskModal: "Create Task",
  DeleteConfirmationModal: "Delete Confirmation",
  EditableField: "Editable Field",
  EditBookingModal: "Edit Booking",
  EditGuestBillModal: "Edit Guest Bill",
  EditGuestModal: "Edit Guest",
  EditOrderModal: "Edit Order",
  EditRoomModal: "Edit Room",
  EditUserModal: "Edit User",
  EstablishmentSetupPage: "Establishment Setup",
  EventsList: "Events List",
  EventsListPage: "Events List",
  EventsPage: "Events",
  FacilitiesPage: "Facilities",
  FeaturesPage: "Features",
  FinancialReportsPage: "Financial Reports",
  FloorPlansPage: "Floor Plans",
  GuestBillingPage: "Guest Billing",
  GuestListPage: "Guest List",
  GuestProfilesPage: "Guest Profiles",
  HousekeeperDashboard: "Housekeeper Dashboard",
  HousekeepingDashboard: "Housekeeping Dashboard",
  HousekeepingModal: "Housekeeping Modal",
  HousekeepingPage: "Housekeeping",
  HousekeepingReportsPage: "Housekeeping Reports",
  KycApprovalPage: "KYC Approval",
  KycApprove: "KYC Approve",
  KycReject: "KYC Reject",
  KYCRejectionPage: "KYC Rejection",
  KycReviewPage: "KYC Review",
  KYCUploadPage: "KYC Upload",
  KYCVerificationPage: "KYC Verification",
  MaintenanceDashboard: "Maintenance Dashboard",
  MaintenancePage: "Maintenance Task",
  MaintenanceRequestForm: "Request Maintenance",
  ManageStaffPage: "Manage Staff",
  ManageUsers: "Manage Users",
  MinimalBookingForm: "Minimal Booking Form",
  Modal: "Modal",
  ModuleAssignment: "Module Assignment",
  NotFound: "Not Found",
  PricingPage: "Pricing",
  ProfileSettings: "Profile Settings",
  ProgressBar: "Progress Bar",
  PromotionsPage: "Promotions",
  RatesPage: "Rates",
  RejectKyc: "Reject KYC",
  ReportsPage: "Reports",
  RequestResetPassword: "Request Reset Password",
  RequestServiceModal: "Request Service",
  ReservatioModal: "Reservatio Modal",
  ReservationCalendar: "Reservation Calendar",
  ReservationCalendarPage: "Reservation Calendar",
  ReservationGantt: "Reservation Gantt",
  ReservationGanttWrapper: "Reservation Gantt Wrapper",
  ReservationReportsPage: "Reservation Reports",
  RestaurantOrderPage: "Restaurant Orders",
  RoleCreationModal: "Role Creation",
  RoomBookingCalendar: "Room Booking Calendar",
  RoomDropdwn: "Room Dropdown",
  RoomImagesPage: "Room Images",
  RoomListPage: "Room List",
  RoomMaintenancePage: "Room Maintenance",
  RoomPricePage: "Room Price",
  RoomQRPage: "Room QR",
  RoomServiceRequestsPage: "Room Service Requests",
  RoomStatusPage: "Room Status",
  RoomTypesPage: "Room Types",
  SearchGuestPage: "Search Guest",
  SetupHotel: "Setup Hotel",
  SetupHotelPage: "Setup Hotel",
  Spinner: "Spinner",
  Staff: "Staff",
  StaffList: "Staff List",
  TasksPage: "Tasks",
  tesmp: "Tesmp",
  UserDashboard: "User Dashboard",
  UsersPage: "Users",
  SystemLogPage: "System Logs",
  CssEditorPage: "CSS Editor",
};

function ModuleAssignmentInner() {
  useFlatPage();
  const queryClient = useQueryClient();

  /* ───────── state ───────── */
  const { authData } = useAuth();
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [view, setView] = useState("general"); // 'general' | 'admin'
  const [search, setSearch] = useState("");

  /* ───────── grouping logic ───────── */
  const mergedModules = useMemo(() => {
    const merged = (modules || []).map((m) => {
      const a = (assignments || []).find((x) => x.moduleId === m.moduleId);
      return a ? { ...m, modulePermissionId: a.modulePermissionId } : m;
    });

    // If some modules have no modulePermissionId yet, keep them but they will render disabled.
    return merged;
  }, [modules, assignments]);

  const isSystemAdminUser = useMemo(
    () => authData?.role?.toLowerCase?.() === "systemadmin",
    [authData]
  );

  const generalModulesGroup = useMemo(
    () =>
      mergedModules.filter(
        (m) =>
          m.controllerName !== "SystemAdminController" &&
          m.controllerName !== "StylesController"
      ),
    [mergedModules]
  );

  const adminModulesGroup = useMemo(
    () =>
      mergedModules.filter(
        (m) =>
          m.controllerName === "SystemAdminController" ||
          m.controllerName === "StylesController"
      ),
    [mergedModules]
  );

  const filteredGeneral = useMemo(() => {
    if (!search) return generalModulesGroup;
    const s = search.toLowerCase();
    return generalModulesGroup.filter((m) =>
      (menuLabelMap[m.moduleName] || m.moduleName).toLowerCase().includes(s)
    );
  }, [generalModulesGroup, search]);

  const filteredAdmin = useMemo(() => {
    if (!search) return adminModulesGroup;
    const s = search.toLowerCase();
    return adminModulesGroup.filter((m) =>
      (menuLabelMap[m.moduleName] || m.moduleName).toLowerCase().includes(s)
    );
  }, [adminModulesGroup, search]);

  /* ───────── owner lock logic ───────── */
  const selectedRole = useMemo(() => {
    return roles.find((r) => r?.id?.toString?.() === roleId) || null;
  }, [roles, roleId]);

  const isOwnerRoleSelected = useMemo(() => {
    return (selectedRole?.name || "").toLowerCase() === "owner";
  }, [selectedRole]);

  // Lock ModuleAssignment for Owner so it can’t be unchecked/cleared
  const lockedPermissionIds = useMemo(() => {
    if (!isOwnerRoleSelected) return new Set();

    const ids = (mergedModules || [])
      .filter((m) => (m.moduleName || "").toLowerCase() === "moduleassignment")
      .map((m) => m.modulePermissionId)
      .filter(Boolean);

    return new Set(ids);
  }, [isOwnerRoleSelected, mergedModules]);

  // Ensure locked IDs always stay selected
  useEffect(() => {
    if (!lockedPermissionIds.size) return;

    setSelectedPermissionIds((prev) => {
      const next = new Set(prev || []);
      lockedPermissionIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [lockedPermissionIds]);

  /* ───────── counters ───────── */
  const totalModules = mergedModules.length;
  const totalGeneral = generalModulesGroup.length;
  const totalAdmin = adminModulesGroup.length;
  const totalSelected = selectedPermissionIds.length;
  const totalRoles = roles.length;

  /* ───────── data fetching ───────── */
  const fetchModules = useCallback(async () => {
    const { data } = await axiosInstance.get("/modules");
    setModules(data || []);
  }, []);

  const fetchAssignments = useCallback(async () => {
    const { data } = await axiosInstance.get("/modulepermissions");
    setAssignments(data || []);
  }, []);

  const fetchRoles = useCallback(async () => {
    if (!authData?.hotelId) {
      setRoles([]);
      return;
    }
    const { data } = await axiosInstance.get(`/roles/byhotel/${authData.hotelId}`);
    setRoles(data || []);
  }, [authData?.hotelId]);

  const fetchRoleAssignments = useCallback(
    async (rid) => {
      if (!rid) {
        setSelectedPermissionIds([]);
        return;
      }

      const { data } = await axiosInstance.get(`/modulepermissions/byrole/${rid}`);
      let ids = (data || []).map((r) => r.modulePermissionId).filter(Boolean);

      // Your existing logic: auto-include admin modules for Owner
      const sel = roles.find((r) => r?.id?.toString?.() === rid);
      if (sel?.name?.toLowerCase?.() === "owner") {
        const extra = adminModulesGroup.map((m) => m.modulePermissionId).filter(Boolean);
        ids = [...new Set([...ids, ...extra])];
      }

      // Re-assert locks (ModuleAssignment) for Owner
      if ((sel?.name || "").toLowerCase() === "owner" && lockedPermissionIds.size) {
        ids = [...new Set([...ids, ...Array.from(lockedPermissionIds)])];
      }

      setSelectedPermissionIds(ids);
    },
    [roles, adminModulesGroup, lockedPermissionIds]
  );

  useEffect(() => {
    (async () => {
      try {
        setBootLoading(true);
        setError(null);
        await Promise.all([fetchModules(), fetchRoles(), fetchAssignments()]);
      } catch (e) {
        console.error(e);
        setError("Failed to load data.");
      } finally {
        setBootLoading(false);
      }
    })();
  }, [fetchModules, fetchRoles, fetchAssignments]);

  useEffect(() => {
    if (!roles.length) return;
    // default to "User" if present
    const userRole = roles.find((r) => r?.name?.toLowerCase?.() === "user") || roles[0];
    if (userRole?.id) setRoleId(userRole.id.toString());
  }, [roles]);

  useEffect(() => {
    if (roleId) fetchRoleAssignments(roleId);
  }, [roleId, fetchRoleAssignments]);

  /* ───────── handlers ───────── */
  const onToggleGroup = (group, selectAll) => {
    const ids = group.map((m) => m.modulePermissionId).filter(Boolean);

    setSelectedPermissionIds((prev) => {
      const current = new Set(prev || []);

      if (selectAll) {
        ids.forEach((id) => current.add(id));
        lockedPermissionIds.forEach((id) => current.add(id));
        return Array.from(current);
      }

      // Clear: remove only non-locked items
      ids.forEach((id) => {
        if (!lockedPermissionIds.has(id)) current.delete(id);
      });

      // Re-assert locks
      lockedPermissionIds.forEach((id) => current.add(id));
      return Array.from(current);
    });
  };

  const onSubmit = async () => {
    if (!roleId) {
      notify.warning("Please select a role first.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const finalIds = Array.from(
        new Set([...(selectedPermissionIds || []), ...Array.from(lockedPermissionIds)])
      );

      await axiosInstance.post("/modulepermissions/assign", {
        RoleId: roleId,
        ModuleIds: finalIds,
      });

      // Menu uses queryKey: ["modules", roleId, hotelId] — invalidate broadly
      queryClient.invalidateQueries({ queryKey: ["modules"] });

      hub.publish("permissionsChanged", { roleId, moduleIds: finalIds });

      notify.success({
        message: "Permissions Saved",
        description: "Role permissions have been updated and propagated to active sessions.",
      });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || "Error saving assignment.";
      setError(msg);
      notify.error({
        message: "Save Failed",
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const getModuleLabel = (name) => menuLabelMap[name] || name;

  /* ───────── render helpers ───────── */
  // Modules sit in the same groups, in the same order, as the sidebar (Front Office, Rooms, ...).
  const groupModules = (items) => {
    const byGroup = {};
    items.forEach((m) => {
      const key = COMPONENT_GROUP_MAP[m.moduleName] && GROUP_DEFS[COMPONENT_GROUP_MAP[m.moduleName]] ? COMPONENT_GROUP_MAP[m.moduleName] : "other";
      (byGroup[key] ||= []).push(m);
    });
    return Object.keys(GROUP_DEFS)
      .filter((k) => byGroup[k]?.length)
      .map((k) => ({
        ...GROUP_DEFS[k],
        items: byGroup[k].slice().sort((x, y) => {
          const ox = GROUP_ITEM_ORDER[x.moduleName] ?? 999;
          const oy = GROUP_ITEM_ORDER[y.moduleName] ?? 999;
          return ox - oy || getModuleLabel(x.moduleName).localeCompare(getModuleLabel(y.moduleName));
        }),
      }));
  };

  const setGroupSelected = (ids, on) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev || []);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      lockedPermissionIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const renderModuleList = (items) => {
    if (bootLoading) return <Skeleton active paragraph={{ rows: 4 }} />;

    if (!items.length) return <div className="hms-empty">No modules match your search.</div>;

    // Only modules with a modulePermissionId can be selected.
    const selectableIds = items.map((i) => i.modulePermissionId).filter(Boolean);
    const groups = groupModules(items);

    return (
      <Checkbox.Group
        style={{ width: "100%", display: "block" }}
        value={selectedPermissionIds}
        onChange={(vals) => {
          // Keep selections from other panels, but replace within this panel
          const others = (selectedPermissionIds || []).filter((id) => !selectableIds.includes(id));
          const nextWithinPanel = (vals || []).filter((v) => selectableIds.includes(v));

          const next = new Set([...others, ...nextWithinPanel]);
          lockedPermissionIds.forEach((id) => next.add(id));

          setSelectedPermissionIds(Array.from(next));
        }}
      >
        <div className="ma-groups">
          {groups.map((g) => {
            const ids = g.items.map((m) => m.modulePermissionId).filter(Boolean);
            const selected = ids.filter((id) => selectedPermissionIds.includes(id)).length;
            const Icon = g.icon;
            return (
              <section key={g.key} className="ma-group">
                <header className="ma-group-head">
                  <span className="ma-group-title">
                    {Icon && <Icon />}
                    {g.label}
                    <span className={`ma-group-count ${selected === ids.length && ids.length ? "is-full" : ""}`}>
                      {selected}/{g.items.length}
                    </span>
                  </span>
                  <span className="ma-group-actions">
                    <button type="button" className="ma-link" disabled={!ids.length} onClick={() => setGroupSelected(ids, true)}>
                      All
                    </button>
                    <button type="button" className="ma-link" disabled={!ids.length} onClick={() => setGroupSelected(ids, false)}>
                      None
                    </button>
                  </span>
                </header>

                <ul className="ma-group-list">
                  {g.items.map((m) => {
                    const id = m.modulePermissionId;
                    const locked = !!id && lockedPermissionIds.has(id);
                    const disabled = !id || locked;

                    const disabledReason = !id
                      ? "This module has no permission record (modulePermissionId) yet."
                      : locked
                      ? "Required for Owner role. Cannot be removed."
                      : undefined;

                    return (
                      <li key={id ?? `${m.moduleId}-${m.moduleName}`} className={`ma-row ${disabled ? "is-disabled" : ""}`}>
                        <Tooltip title={disabled ? disabledReason : undefined}>
                          <Checkbox value={id} disabled={disabled}>
                            {getModuleLabel(m.moduleName)}
                            {locked ? " (required for Owner)" : ""}
                          </Checkbox>
                        </Tooltip>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </Checkbox.Group>
    );
  };

  const generalAllSelected =
    totalGeneral > 0 &&
    generalModulesGroup
      .map((m) => m.modulePermissionId)
      .filter(Boolean)
      .every((id) => selectedPermissionIds.includes(id));

  const adminAllSelected =
    totalAdmin > 0 &&
    adminModulesGroup
      .map((m) => m.modulePermissionId)
      .filter(Boolean)
      .every((id) => selectedPermissionIds.includes(id));

  /* ───────── UI ───────── */
  const currentGroup = view === "general" ? generalModulesGroup : adminModulesGroup;
  const currentAllSelected = view === "general" ? generalAllSelected : adminAllSelected;
  const visibleCount = view === "general" ? filteredGeneral.length : filteredAdmin.length;

  return (
    <div className="hms-page ma-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfo(true)}>
              Guide
            </Button>
            <Button icon={<IconPlus />} onClick={() => setShowRoleModal(true)}>
              Add role
            </Button>
            <Button type="primary" loading={loading} onClick={onSubmit} disabled={!roleId}>
              Save changes
            </Button>
          </>
        }
      />

      {error && (
        <Alert style={{ marginBottom: 12 }} type="error" title="Operation failed" description={error} showIcon />
      )}

      <div className="hms-stack">
        <SectionCard
          flush
          title="Module access"
          extra={
            <Select
              style={{ width: 260 }}
              placeholder="Select a role"
              value={roleId || undefined}
              onChange={setRoleId}
              options={roles.map((r) => ({ value: r.id.toString(), label: r.name }))}
            />
          }
        >
          <StatStrip
            items={[
              { label: "Modules", value: totalModules },
              { label: "Selected for this role", value: totalSelected, tone: totalSelected ? "good" : undefined },
              { label: "General modules", value: totalGeneral },
              ...(isSystemAdminUser ? [{ label: "Admin modules", value: totalAdmin }] : []),
              { label: "Roles", value: totalRoles },
            ]}
          />
        </SectionCard>

        <SectionCard
          flush
          title={view === "general" ? "General modules" : "Administration modules"}
          extra={
            <ListToolbar search={{ value: search, onChange: setSearch, placeholder: "Search modules" }}>
              <StatusTag status={currentAllSelected ? "available" : ""}>{currentAllSelected ? "All selected" : "Partial"}</StatusTag>
              <Button onClick={() => onToggleGroup(currentGroup, true)} disabled={!currentGroup.length}>
                Select all
              </Button>
              <Button onClick={() => onToggleGroup(currentGroup, false)} disabled={!currentGroup.length}>
                Clear
              </Button>
            </ListToolbar>
          }
        >
          <TabBar
            tabs={[
              { key: "general", label: "General", count: totalGeneral },
              ...(isSystemAdminUser ? [{ key: "admin", label: "Administration", count: totalAdmin }] : []),
            ]}
            value={view}
            onChange={setView}
          />

          <div style={{ padding: 20 }}>
            <div className="hms-card-hint" style={{ padding: "0 0 12px" }}>
              Showing {visibleCount} {search ? "matching " : ""}module{visibleCount === 1 ? "" : "s"}
            </div>
            {view === "general" ? (
              renderModuleList(filteredGeneral)
            ) : isSystemAdminUser ? (
              renderModuleList(filteredAdmin)
            ) : (
              <Alert type="info" title="Administration modules are visible only to System Admin users." />
            )}
          </div>
        </SectionCard>
      </div>

      {/* Role modal */}
      <RoleCreationModal
        open={showRoleModal}
        onCancel={() => setShowRoleModal(false)}
        onRoleCreated={(newRole) => {
          setRoles((prev) => [...prev, newRole]);
          setRoleId(newRole?.id?.toString?.() || "");
        }}
        hotelId={authData?.hotelId}
      />

      {/* Guide */}
      <Modal
        title="Module assignment guide"
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>,
        ]}
        width={600}
        destroyOnHidden
      >
        <p className="hms-modal-note">
          Choose which parts of the app a role can open. Changes reach open sessions straight away, so menus update without a
          reload.
        </p>

        <Section title="Quick steps">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Select a role, or add a new one.</li>
            <li>Use Select all, or tick the modules you want.</li>
            <li>Click Save changes.</li>
          </ol>
        </Section>

        <Section title="Good to know">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Administration modules are visible only to System Admin users.</li>
            <li>For the Owner role, Module Assignment is required and cannot be removed.</li>
            <li>Modules are grouped like the sidebar. Use All / None on a group, or the search box to filter by name.</li>
          </ul>
        </Section>

        <Section title="Right now">
          <DetailList
            items={[
              { label: "Modules", value: totalModules },
              { label: "Selected for this role", value: totalSelected },
              { label: "General modules", value: totalGeneral },
              isSystemAdminUser ? { label: "Admin modules", value: totalAdmin } : null,
              { label: "Roles", value: totalRoles },
            ]}
          />
        </Section>
      </Modal>
    </div>
  );
}

export default function ModuleAssignment() {
  // Ensures App context exists for App.useApp() (message/notification/modal) in AntD v6.
  return (
    <App>
      <ModuleAssignmentInner />
    </App>
  );
}
